import axios from "axios";
import { useRef, useState, useEffect } from "react";

import Input from '@/components/Input';
import Button from '@/components/Button';
import { getErrorMessage, checkPasswordConditions } from '@/utils/Errors';
import { nowByType } from '@/utils/Dates';

import Dropdown from '@/components/Dropdown';
import Switch from '@/components/Switch';

export default function UpdateUser({t,language,user,apiUrl,token,sendMessage,setRootAnswer,handleLogin,confirmDelete}){

    const divRef = useRef(null); 

    useEffect(() => {
        // scroll to div
        if(divRef.current) {
            divRef.current.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
            });
        }

        // get list of users
        getUsers();

        // get the list of roles
        getRoles();

        // get the list of companies
        getCompanies();
        
    }, []);

    const [ roles, setRoles ] = useState([]);
    const [ rolesList, setRolesList ] = useState([]);
    const getRoles = () => {
        
        var apiPath = '/roles/list';

        const config = {
            headers: { Authorization: `Bearer ${token}` },   
            // params is the same as using ?database=, query params      
            params: {
                language: language,
                companyuser: user?.company
            }  
        };

        var errorMessage = t('root.update_user.errors.roles');
        
        axios.get(apiUrl+apiPath,config)
        .then(function (response) {

            if(response?.data){
          
                const responseData = response.data;                

                // if result is ok (1)
                if(responseData?.result){                                        
                    
                    const data = responseData?.data

                    if(data?.items){                        

                        // get roles id,name for the dropdown
                        const rolesInfo = [];
                        data.items?.forEach((item) => {
                            rolesInfo.push({id:item?.id,name:item?.nom});
                        });
                        setRoles(rolesInfo);
                        setRolesList(rolesInfo);
                    }
                    else{
                        setRoles([]);
                        setRolesList([]);
                    }
                } 
                else{
                    if(responseData?.status && responseData.status === 401){
                        handleLogin();                        
                    }
                    else{
                        // check error message
                        if(responseData?.data?.reasons){
                            errorMessage = getErrorMessage(t,'root.update_user',responseData?.data?.reasons);                                
                        }
                        
                        sendMessage(errorMessage,'error');
                    }                        
                }
            }
            else{                
                sendMessage(errorMessage,'error');                    
            }
        })
        .catch(function (error){
            
            if(error?.status && error.status === 401){
                handleLogin();                        
            }
            else{                        
                sendMessage(errorMessage,'error');
            }
        });
    }

    const [ companies, setCompanies ] = useState([]);
    const [ companiesList, setCompaniesList ] = useState([]);
    const getCompanies = () => {
        
        var apiPath = '/companies/list';

        const config = {
            headers: { Authorization: `Bearer ${token}` },   
            // params is the same as using ?database=, query params      
            params: {
                language: language,
                companyuser: user?.company
            }  
        };

        var errorMessage = t('root.update_user.errors.companies');
        
        axios.get(apiUrl+apiPath,config)
        .then(function (response) {

            if(response?.data){
          
                const responseData = response.data;                

                // if result is ok (1)
                if(responseData?.result){                                        
                    
                    const data = responseData?.data

                    if(data?.items){                        

                        // get roles id,name for the dropdown
                        const companiesInfo = [];
                        data.items?.forEach((item) => {
                            companiesInfo.push({id:item?.id,name:item?.empresa_nom});
                        });
                        setCompanies(companiesInfo);
                        setCompaniesList(companiesInfo);
                    }
                    else{
                        setCompanies([]);
                        setCompaniesList([]);
                    }
                } 
                else{
                    if(responseData?.status && responseData.status === 401){
                        handleLogin();                        
                    }
                    else{
                        // check error message
                        if(responseData?.data?.reasons){
                            errorMessage = getErrorMessage(t,'root.update_user',responseData?.data?.reasons);                                
                        }
                        
                        sendMessage(errorMessage,'error');
                    }                        
                }
            }
            else{                
                sendMessage(errorMessage,'error');                    
            }
        })
        .catch(function (error){
            
            if(error?.status && error.status === 401){
                handleLogin();                        
            }
            else{                        
                sendMessage(errorMessage,'error');
            }
        });
    }

    // initially get list of users
    const [ users, setUsers ] = useState(null);
    const [ usersList, setUsersList ] = useState(null);
    const getUsers = () => {
        
        var apiPath = '/users/list';

        const config = {
            headers: { Authorization: `Bearer ${token}` },   
            // params is the same as using ?database=, query params      
            params: {
                language: language,
                companyuser: user?.company
            }  
        };

        var errorMessage = t('root.update_user.errors.update');
        
        axios.get(apiUrl+apiPath,config)
        .then(function (response) {

            if(response?.data){
          
                const responseData = response.data;                

                // if result is ok (1)
                if(responseData?.result){                                        
                    
                    const data = responseData?.data

                    if(data?.items){

                        setUsers(data.items);

                        // get users id,name for the dropdown
                        const usersInfo = [];
                        data.items?.forEach((item) => {
                            usersInfo.push({id:item?.id,name:item?.name});
                        });
                        setUsersList(usersInfo);
                    }
                    else{
                        setUsers(null);
                    }
                } 
                else{
                    if(responseData?.status && responseData.status === 401){
                        handleLogin();                        
                    }
                    else{
                        // check error message
                        if(responseData?.data?.reasons){
                            errorMessage = getErrorMessage(t,'root.update_user',responseData?.data?.reasons);                                
                        }
                        
                        sendMessage(errorMessage,'error');
                    }                        
                }
            }
            else{                
                sendMessage(errorMessage,'error');                    
            }
        })
        .catch(function (error){
            
            if(error?.status && error.status === 401){
                handleLogin();                        
            }
            else{                        
                sendMessage(errorMessage,'error');
            }
        });

    }    

    const [ errors, setErrors ] = useState(null);
    const [ data, setData ] = useState({
        id: null,
        name: '',        
        password: '',                
        legal: false,
        reset: false,
        active: false,
        roles: [],
        companies: [],
        // default values that cannot be null
        type: 9,
        module: '{7}',
        created: nowByType('datetime')
    });

    const resetData = () => {
        setData({
            id: null,
            name: '',        
            password: '',                
            legal: false,
            reset: false,
            active: false,
            roles: [],
            companies: [],
            // default values that cannot be null
            type: 9,
            module: '{7}',
            created: nowByType('datetime')
        });
    }

    const handleInput = (e) => {
        
        var name = e.target.name;
        var value = e.target.value;

        var newData = {...data};
        newData[name] = value;
        setData(newData);
    };

    const handleInputSwitch = (e) => {
        
        const name = e.target.name;
        var value = e.target.checked;
        
        var newData = {...data};
        newData[name] = value;
        setData(newData);
    }

    // manage roles    
    const handleRoles = (dropdownData) => {       
        var newData = {...data};
        newData['roles'] = dropdownData;
        setData(newData);
    }

    // manage companies    
    const handleCompanies = (dropdownData) => {       
        var newData = {...data};
        newData['companies'] = dropdownData;
        setData(newData);
    }

    const handleSubmit = () => {
        
        var errorsCheck = {};

        // check password conditions if we want to change it
        if(data.password.length > 0){
            const errorsPassword = checkPasswordConditions(data.password);
            const keysPassword = Object.keys(errorsPassword);               
            if(keysPassword && keysPassword?.length > 0){
                errorsCheck = {...errorsCheck,...errorsPassword};
            }
        }

        if(Object.keys(errorsCheck).length === 0){
            
            const apiPath = '/users/edit';

            const config = {
                headers: { Authorization: `Bearer ${token}` } 
            };

            const send = {...data};

            send.language = language;
            send.companyuser = user?.company;

            // change roles to csv values
            if(data?.roles){
                send.roles = data?.roles?.map(role => role.id)?.join(',');
            }

            // change companies to csv values
            if(data?.companies){
                send.companies = data?.companies?.map(company => company.id)?.join(',');
            }

            var errorMessage = t('root.update_user.errors.update');
            
            const url = apiUrl+apiPath;
            
            axios.put(url,send,config)
            .then(function (response) {

                if(response?.data){
              
                    const responseData = response.data;                

                    // if result is ok (1)
                    if(responseData?.result){

                        sendMessage(t('root.update_user.updated'));
                        
                        const dataUpdated = responseData?.data;

                        // if it is myself, logout to get new values
                        if(parseInt(user?.id) === data?.id){
                            handleLogin(null,null,true);
                        }
                        else{
                            const render = 
                                <div className='mt-4'>                                       
                                    <div className="text-xl font-bold mb-2">
                                        {t('root.update_user.updated')}
                                    </div>                        
                                    <div className="">
                                        {
                                            dataUpdated?.item && Object.keys(dataUpdated?.item)?.map((key) => (
                                                <div key={'root-action-update-user-item-'+dataUpdated.item?.id+'-column-'+key}>
                                                    <b>{key}</b>: {
                                                        dataUpdated.item[key] !== null && typeof dataUpdated.item[key] === 'object' ?
                                                            Object.keys(dataUpdated.item[key])?.map(subkey => dataUpdated.item[key][subkey]['name']).join(' / ')
                                                        :
                                                            dataUpdated.item[key]
                                                    }
                                                </div>
                                            ))
                                        }
                                        <div className='mt-2 text-center'>
                                            <Button
                                                type='delete'
                                                variant='contained'
                                                text={t('actions.delete.button')}
                                                onClick={() => confirmDelete(dataUpdated?.item['id'])}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ;
                            
                            setRootAnswer(render);
                        }
                    } 
                    else{
                        if(responseData?.status && responseData.status === 401){
                            handleLogin();                        
                        }
                        else{
                            // check error message
                            if(responseData?.data?.reasons){
                                errorMessage = getErrorMessage(t,'root.update_user',responseData?.data?.reasons);                                
                            }
                            
                            sendMessage(errorMessage,'error');
                        }                        
                    }
                }
                else{                
                    sendMessage(errorMessage,'error');                    
                }
            })
            .catch(function (error){
                
                if(error?.status && error.status === 401){
                    handleLogin();                        
                }
                else{                        
                    sendMessage(errorMessage,'error');
                }
            });
        }
        else{            
            const errorMessage = getErrorMessage(t,'root.update_user',errorsCheck);
            setErrors({password:'password'});
            sendMessage(errorMessage,'error');
        }
    }

    // on user selected
    const [ userEdit, setUserEdit ] = useState(null);
    const handleUser = (newItem) => {       

        if(!newItem){
            setUserEdit(null);
            resetData();
        }
        else{
            setUserEdit({id:newItem?.id,name:newItem?.name});

            // get user values
            var newUsers = users?.filter((v) => v?.id === newItem?.id);

            if(newUsers?.length > 0){

                const newUser = newUsers.pop();

                const name = newUser && Object.hasOwn(newUser,'usu_nom') ? newUser['usu_nom'] : newItem?.name;

                // get roles list
                var rolesArray = [];
                if(newUser?.roles && newUser?.roles?.length > 0){

                    const rolesIds = [];
                    newUser?.roles?.forEach((role) => {
                        rolesArray.push({id:role?.id,name:role?.name});
                        rolesIds.push(role?.id);
                    });

                    // remove from global list the chosen ones                    
                    const filterRoles = roles?.filter((v) => !rolesIds.includes(v.id));
                    setRolesList(filterRoles);
                }

                // get companies list
                var companiesArray = [];
                if(newUser?.companies && newUser?.companies?.length > 0){

                    const companiesIds = [];
                    newUser?.companies?.forEach((company) => {
                        companiesArray.push({id:company?.id,name:company?.name});
                        companiesIds.push(company?.id);
                    });

                    // remove from global list the chosen ones                    
                    const filterCompanies = companies?.filter((v) => !companiesIds.includes(v.id));
                    setCompaniesList(filterCompanies);
                }

                const legal = newUser && Object.hasOwn(newUser,'usu_accepta_clausula_privacitat') ? 
                        newUser['usu_accepta_clausula_privacitat'] ? 
                            true
                        :
                            false
                    : 
                        false
                ;

                // reset 0 means ok, reset 1 means reset password
                // on database is the opposite, 0 means reset, 1 not
                const reset = newUser && Object.hasOwn(newUser,'usu_restableix_contrasenya') ? 
                        newUser['usu_restableix_contrasenya'] ? 
                            false
                        :
                            true
                    : 
                        false
                ;

                const active = newUser && Object.hasOwn(newUser,'usu_actiu') ? 
                        newUser['usu_actiu'] ? 
                            true
                        :
                            false
                    : 
                        false
                ;

                setData({
                    id: newItem?.id,
                    name: name,
                    password: '',
                    legal: legal,
                    reset: reset,
                    active: active,
                    roles: rolesArray,
                    companies: companiesArray,
                    // default values that cannot be null
                    type: 9,
                    module: '{7}',
                    created: nowByType('datetime')
                });
            }
            else{
                resetData();
            }
        }
    }

    // to log out the user
    const handleLogout = () => {

        const apiPath = '/users/logout';

        const config = {
            headers: { Authorization: `Bearer ${token}` } 
        };

        const send = {
            language: language,
            companyuser: user?.company,
            user: data?.id
        };

        var errorMessage = t('root.logout_user.errors.logout');
        
        const url = apiUrl+apiPath;
        
        axios.put(url,send,config)
        .then(function (response) {

            if(response?.data){
          
                const responseData = response.data;                

                // if result is ok (1)
                if(responseData?.result){

                    sendMessage(t('root.logout_user.success'));

                    // if it is myself, logout
                    if(parseInt(user?.id) === data?.id){
                        handleLogin(null,null,true);
                    }
                } 
                else{
                    if(responseData?.status && responseData.status === 401){
                        handleLogin();                        
                    }
                    else{
                        // check error message
                        if(responseData?.data?.reasons){
                            errorMessage = getErrorMessage(t,'root.logout_user',responseData?.data?.reasons);                                
                        }
                        
                        sendMessage(errorMessage,'error');
                    }                        
                }
            }
            else{                
                sendMessage(errorMessage,'error');                    
            }
        })
        .catch(function (error){
            
            if(error?.status && error.status === 401){
                handleLogin();                        
            }
            else{                        
                sendMessage(errorMessage,'error');
            }
        });
    }

	return (
        <div className='mt-4' ref={divRef}>
            <div className='mb-4'>
                {t('root.update_user.description')}
            </div>
            <div className='mb-4'>                    
                <Dropdown                    
                    type='search'
                    name='user'
                    value={userEdit}
                    options={usersList}
                    handleDropdown={handleUser}
                    //error={}
                />
            </div>
            <div className='mb-4'>
                <Input                      
                    name="name"
                    type="text"
                    value={data.name}                      
                    placeholder={t('root.update_user.fields.name')}                    
                    onChange={handleInput}
                    error={errors && errors?.name ? t('root.update_user.errors.'+errors.name) : ''}
                    disabled={!userEdit}
                    //handleKeyDown={}
                />
            </div>
            <div className='mb-4'>
                <Input                      
                    name="password"
                    type="text"
                    value={data.password}                      
                    placeholder={t('root.update_user.fields.password')}                    
                    onChange={handleInput}
                    error={errors && errors?.password ? t('root.update_user.errors.'+errors.password) : ''}
                    disabled={!userEdit}
                    //handleKeyDown={}
                />
            </div>
            <div className='mb-4'>
                <Dropdown                    
                    type='multiple'
                    name='roles'
                    value={data.roles}
                    options={rolesList}
                    handleDropdown={handleRoles}
                    disabled={!userEdit}
                    //error={}
                />
            </div>
            <div className='mb-4'>
                <Dropdown                    
                    type='multiple'
                    name='companies'
                    value={data.companies}
                    options={companiesList}
                    handleDropdown={handleCompanies}
                    disabled={!userEdit}
                    //error={}
                />
            </div>            
            <div className='mb-4'>
                <Switch                                                                    
                    name='legal'
                    checked={data.legal}
                    label={t('root.update_user.fields.legal')}
                    onChange={handleInputSwitch}                              
                />
            </div>
            <div className='mb-4'>
                <Switch                                                                    
                    name='reset'
                    checked={data.reset}
                    label={t('root.update_user.fields.reset')}
                    onChange={handleInputSwitch}                              
                />
            </div>
            <div className='mb-4'>
                <Switch                                                                    
                    name='active'
                    checked={data.active}
                    label={t('root.update_user.fields.active')}
                    onChange={handleInputSwitch}                              
                />
            </div>
            <div className='flex justify-center'>
                <div className='me-1'>
                    <Button                     
                        onClick={handleSubmit}
                        text={t('root.update_user.button')}
                        type='accept'   
                        disabled={!userEdit}         
                    />
                </div>
                <div className='me-1'>
                    <Button                     
                        onClick={handleLogout}
                        text={t('root.logout_user.button')}
                        type='back'   
                        disabled={!userEdit}         
                    />
                </div>
            </div>
        </div>
    );
}